/*
 * MicAnalysis — gain-invariant, DSP-aware microphone diagnostic engine.
 *
 * The core fix for the mic-test "unstable A→D / recommends the built-in mic" bug:
 * NO absolute dBFS level is ever an input to the verdict. Absolute dBFS depends on
 * sensitivity × gain × room — not on microphone quality — so a sensitive external
 * mic reads a higher floor than a gated phone mic for the SAME room and loses.
 * Every axis here is gain-invariant (SNR, crest, bandwidth shape) or a robust
 * statistic, and device processing (AGC / noise-suppression / gating) is actively
 * detected and prevented from earning credit. Result: the verdict is stable when
 * Android AGC re-rides the gain, and an honest mic is never beaten by a lying one.
 *
 * Pure functions only — the blade collects raw capture data and calls analyze().
 * UMD-lite footer so the Node regression harness tests THIS shipped code.
 */
(function (root) {
    'use strict';

    var NC = (root && root.NoiseClassifier) ||
        (typeof require !== 'undefined' ? require('./noise-classifier.js') : null);

    /* ---- small stats helpers ---- */
    function toDb(lin) { return lin < 1e-10 ? -100 : 20 * Math.log10(lin); }
    function clamp(v, lo, hi) { return Math.max(lo, Math.min(hi, v)); }
    function mean(a) { if (!a.length) return 0; var s = 0; for (var i = 0; i < a.length; i++) s += a[i]; return s / a.length; }
    function percentile(arr, p) {
        if (!arr.length) return 0;
        var s = arr.slice().sort(function (x, y) { return x - y; });
        return s[clamp(Math.floor(s.length * p), 0, s.length - 1)];
    }
    function stdevDb(rmsLinArr) {
        /* stddev of window levels expressed in dB — measures how "alive" the floor is */
        if (rmsLinArr.length < 2) return 0;
        var dbs = rmsLinArr.map(toDb).filter(function (d) { return d > -100; });
        if (dbs.length < 2) return 0;
        var m = mean(dbs), v = 0;
        for (var i = 0; i < dbs.length; i++) { var d = dbs[i] - m; v += d * d; }
        return Math.sqrt(v / dbs.length);
    }

    /* ---- robust levels ---- */
    /* Noise floor: 25th-percentile of silence-window RMS. Robust to a brief cough
       (which only lifts the top percentiles) yet biased toward the true resting
       floor rather than the mean. */
    function noiseFloorDb(rmsWindows) {
        if (!rmsWindows || !rmsWindows.length) return -100;
        return toDb(percentile(rmsWindows, 0.25));
    }
    /* Speech level: median of VAD-active windows (windows louder than gate). Both
       speech and floor pass through the same sensitivity+gain, so SNR cancels it. */
    function speechLevelDb(rmsWindows, floorLin) {
        if (!rmsWindows || !rmsWindows.length) return -100;
        var gate = Math.max(floorLin * 3.16 /* +10 dB */, 1e-6);
        var active = rmsWindows.filter(function (r) { return r > gate; });
        if (active.length < 3) active = rmsWindows; /* fallback: user barely spoke */
        return toDb(percentile(active, 0.5));
    }

    /* ---- bandwidth extent (gain-invariant shape) ----
       On the normalized long-term voiced spectrum, find the −10 dB rolloff points
       vs the peak band. Indicative captured band, NOT a calibrated freq response. */
    function bandwidthExtent(spectrumDb, sampleRate, fftSize) {
        if (!spectrumDb || !spectrumDb.length) return { lo: 0, hi: 0 };
        var binRes = sampleRate / fftSize;
        var loBin = Math.max(1, Math.floor(60 / binRes));
        var hiBin = Math.min(spectrumDb.length - 1, Math.floor(16000 / binRes));
        var peak = -Infinity;
        for (var b = loBin; b <= hiBin; b++) if (spectrumDb[b] > peak) peak = spectrumDb[b];
        if (peak === -Infinity) return { lo: 0, hi: 0 };
        var thr = peak - 10;
        var lo = 0, hi = 0, i;
        for (i = loBin; i <= hiBin; i++) { if (spectrumDb[i] >= thr) { lo = Math.round(i * binRes); break; } }
        for (i = hiBin; i >= loBin; i--) { if (spectrumDb[i] >= thr) { hi = Math.round(i * binRes); break; } }
        return { lo: lo, hi: hi };
    }

    /* ---- device-processing detection (0..1) ----
       See report §1.3: constraint mismatch is the most reliable signal; a collapsed
       AND unnaturally flat floor is the behavioural signature of gating/NS; a floor
       that rises after speech is AGC pumping. Weights tuned so an honest quiet mic
       (low but naturally varying floor, effective flags off) scores ~0, while a
       gated phone (very low + flat + flags stuck on) scores high. */
    function processedScore(input) {
        var flags = [];
        var score = 0;
        var req = input.requested || {}, eff = input.effective || {};
        function mism(k) { return req[k] === false && eff[k] === true; }
        if (mism('noiseSuppression') || mism('autoGainControl') || mism('echoCancellation')) {
            score += 0.4; flags.push('constraint_mismatch');
        }
        var floor = (input.noise && input.noise.floorDb != null)
            ? input.noise.floorDb
            : noiseFloorDb(input.noise && input.noise.rmsWindows);
        var flat = input.noise ? stdevDb(input.noise.rmsWindows || []) < 1.5 : false;
        var collapsed = floor < -72;
        if (collapsed) { score += 0.2; flags.push('floor_collapse'); }
        if (flat && (input.noise.rmsWindows || []).length >= 8) { score += 0.2; flags.push('flat_floor'); }
        if (collapsed && flat) score += 0.15; /* the combination is the real tell */
        /* AGC pumping: floor measured just after speech is well above resting floor */
        if (input.noise && input.noise.postSpeechFloorDb != null &&
            input.noise.postSpeechFloorDb > floor + 6) {
            score += 0.3; flags.push('pumping');
        }
        score = clamp(score, 0, 1);
        return { score: score, flags: flags, detected: score >= 0.5 };
    }

    /* ---- per-axis state machine ---- */
    function levelState(db) {
        if (db >= -1) return 'fail';        /* hot / clip risk */
        if (db <= -40) return 'fail';       /* too quiet to use */
        if (db >= -18 && db <= -9) return 'pass';
        return 'caution';
    }
    function snrState(snr, processedDetected) {
        var s = snr >= 40 ? 'pass' : snr >= 25 ? 'caution' : 'fail';
        /* A suppressed mic can't earn a "clean" SNR — its floor is faked. */
        if (processedDetected && s === 'pass') s = 'caution';
        return s;
    }
    function clipState(clipPct) {
        if (clipPct <= 0) return 'pass';
        if (clipPct < 0.1) return 'caution';
        return 'fail';
    }
    function bandwidthState(bw) {
        if (!bw.hi) return 'na';
        if (bw.hi >= 11000 && bw.lo <= 150) return 'pass';
        if (bw.hi >= 7000) return 'caution';
        return 'fail'; /* telephone band */
    }
    function noiseState(primary, issues) {
        if (primary === 'clean' || !issues.length) return 'pass';
        var top = issues.reduce(function (a, b) {
            var rank = { strong: 3, moderate: 2, mild: 1 };
            return rank[b.severity] > rank[a.severity] ? b : a;
        });
        return top.severity === 'mild' ? 'caution' : 'fail';
    }

    /*
     * analyze(input) → full diagnostic. input shape:
     * {
     *   sampleRate, fftSize, channelCount,
     *   requested:{noiseSuppression,autoGainControl,echoCancellation},
     *   effective:{...from getSettings()},
     *   noise:{ rmsWindows:[lin], spectrumDb:Float32, crestFactors:[], postSpeechFloorDb? },
     *   speech:{ rmsWindows:[lin], spectrumDb:Float32, peak:lin, clipCount, sampleCount },
     *   transient:{ count, peakDb },
     *   latencyMs
     * }
     */
    function analyze(input) {
        input = input || {};
        var sr = input.sampleRate || 48000, fft = input.fftSize || 4096;
        var noise = input.noise || {}, speech = input.speech || {};

        var floorDb = noise.floorDb != null ? noise.floorDb : noiseFloorDb(noise.rmsWindows);
        noise.floorDb = floorDb;
        var floorLin = floorDb <= -100 ? 1e-10 : Math.pow(10, floorDb / 20);
        var speechDb = speech.rmsWindows ? speechLevelDb(speech.rmsWindows, floorLin) : -100;
        var snrDb = Math.max(0, speechDb - floorDb);

        var bw = bandwidthExtent(speech.spectrumDb, sr, fft);
        var peakLin = speech.peak || 0;
        var speechLin = speechDb <= -100 ? 1e-10 : Math.pow(10, speechDb / 20);
        var crestDb = peakLin > 1e-10 && speechLin > 1e-10 ? clamp(toDb(peakLin) - speechDb, 0, 40) : 0;
        var clipCount = speech.clipCount || 0;
        var clipPct = speech.sampleCount ? (clipCount / speech.sampleCount * 100) : 0;

        var cls = NC ? NC.classify(noise.spectrumDb, { sampleRate: sr, fftSize: fft, crestFactors: noise.crestFactors })
            : { issues: [], primary: 'clean' };
        var proc = processedScore(input);
        var trans = input.transient || { count: 0, peakDb: -100 };

        var axes = [];
        function axis(key, state, value, fixKey) { axes.push({ key: key, state: state, value: value, fix: fixKey || null }); }

        var lvlState = levelState(speechDb);
        axis('level', lvlState, { db: Math.round(speechDb) },
            lvlState === 'fail' ? (speechDb >= -1 ? 'fix_hot' : 'fix_quiet') : (lvlState === 'caution' ? 'fix_level' : null));

        var sState = snrState(snrDb, proc.detected);
        // When a high SNR is only "caution" because processing is inflating it, the
        // fix is NOT "speak closer" — it's that the number is faked by suppression.
        var snrFix = sState === 'pass' ? null : (snrDb >= 40 && proc.detected ? 'fix_snr_processed' : 'fix_snr');
        axis('snr', sState, { db: Math.round(snrDb) }, snrFix);

        var bState = bandwidthState(bw);
        axis('bandwidth', bState, { lo: bw.lo, hi: bw.hi }, bState === 'fail' ? 'fix_bandwidth' : null);

        var cState = clipState(clipPct);
        axis('clip', cState, { count: clipCount }, cState !== 'pass' ? 'fix_clip' : null);

        var nState = noiseState(cls.primary, cls.issues);
        axis('noise', nState, { primary: cls.primary, issues: cls.issues }, nState !== 'pass' ? ('noisefix_' + cls.primary) : null);

        axis('processing', proc.detected ? 'caution' : 'pass', { score: proc.score, flags: proc.flags },
            proc.detected ? 'fix_processing' : null);

        var tState = trans.count >= 1 ? 'pass' : (trans.attempted ? 'caution' : 'na');
        axis('transient', tState, { count: trans.count, peakDb: Math.round(trans.peakDb) }, null);

        /* Verdict tiers. Core axes can push to 'problem'; info axes only to 'usable'. */
        var CORE = { level: 1, snr: 1, clip: 1, noise: 1 };
        var verdict = 'ready';
        axes.forEach(function (a) {
            if (a.state === 'fail') verdict = CORE[a.key] ? 'problem' : (verdict === 'ready' ? 'usable' : verdict);
            else if (a.state === 'caution' && verdict === 'ready') verdict = 'usable';
        });

        /* Three-way split summary states for the Mic / Room / Processing cards. */
        var micState = worst([sState, bState, cState]);          /* the transducer+path */
        var roomState = nState === 'na' ? 'pass' : nState;       /* environment noise */
        var procState = proc.detected ? 'caution' : 'pass';      /* OS/browser DSP */

        return {
            floorDb: floorDb, speechDb: speechDb, snrDb: snrDb, bandwidth: bw,
            crestDb: crestDb, clipCount: clipCount, clipPct: clipPct,
            noise: cls, processed: proc, transient: trans,
            latencyMs: input.latencyMs != null ? input.latencyMs : -1,
            axes: axes, verdict: verdict,
            mic: { state: micState }, room: { state: roomState, primary: cls.primary },
            processing: { state: procState, detected: proc.detected, flags: proc.flags },
            confidence: proc.detected ? 'approx' : 'high'
        };
    }

    function worst(states) {
        var rank = { fail: 3, caution: 2, na: 0, pass: 1 };
        return states.reduce(function (w, s) { return (rank[s] || 0) > (rank[w] || 0) ? s : w; }, 'pass');
    }

    var API = {
        analyze: analyze,
        noiseFloorDb: noiseFloorDb,
        speechLevelDb: speechLevelDb,
        bandwidthExtent: bandwidthExtent,
        processedScore: processedScore,
        _helpers: { toDb: toDb, percentile: percentile, stdevDb: stdevDb, clamp: clamp }
    };
    if (typeof module !== 'undefined' && module.exports) module.exports = API;
    if (root) root.MicAnalysis = API;
})(typeof window !== 'undefined' ? window : (typeof globalThis !== 'undefined' ? globalThis : null));
